/**
 * Feature Flag Configuration for Memoraize
 * 
 * KISS Approach: Only premium/controlled features are listed here.
 * Core features (assignments, attendance, messaging) are available to all users by default.
 * 
 * Tier Structure:
 * - essential: Core madrasah management features
 * - professional: Essential + AI insights, advanced analytics  
 * - enterprise: Professional + multi-location, custom integrations (future)
 */

export const FEATURES = {
  // Professional tier features - currently live
  correlationInsights: {
    tier: 'enterprise',
    enabled: false,  // Beta-only feature
    beta: ['67fd1f37d27bc9fbd4ed0e62', '67f23e53eccc3b2351b40778'],  // Dev and Prod test centers
    description: 'AI-powered student performance correlation insights (BETA)'
  },
  
  topPerformers: { 
    tier: 'professional', 
    enabled: true,
    description: 'Top performing students dashboard widget'
  },
  
  studentsNeedingSupport: {
    tier: 'professional',
    enabled: true,
    description: 'Students requiring additional support insights'
  },
  
  // Professional tier features - in development
  behaviorTracking: { 
    tier: 'professional', 
    enabled: false, 
    beta: [],
    description: 'Advanced student behavior pattern tracking'
  },
  
  advancedReporting: { 
    tier: 'professional', 
    enabled: false,
    description: 'Enhanced analytics and reporting capabilities'
  },
  
  // Future enterprise features (18+ months)
  multiLocation: { 
    tier: 'enterprise', 
    enabled: false,
    description: 'Multi-location center management'
  },
  
  customIntegrations: { 
    tier: 'enterprise', 
    enabled: false,
    description: 'Custom API integrations and webhooks'
  }
};

/**
 * Check if a feature is accessible to a center
 * @param {string} featureName - Name of the feature to check
 * @param {Object} centerData - Center object with licenseLevel property
 * @returns {boolean} - True if feature is accessible
 */
export const canAccess = (featureName, centerData) => {
  const feature = FEATURES[featureName];
  
  // Feature not in flags list = available to everyone (KISS principle)
  if (!feature) {
    return true;
  }
  
  // Check if feature is enabled at all
  if (!feature.enabled) {
    // Check if center has beta access (try both id and _id fields)
    const centerId = centerData?.id || centerData?._id;
    if (feature.beta && centerId && feature.beta.includes(centerId)) {
      return true;
    }
    return false;
  }
  
  // Check if center data exists
  if (!centerData || !centerData.licenseLevel) {
    console.warn('canAccess: Missing center data or license level');
    return false;
  }
  
  // Check license tier access
  const tierRank = { 
    essential: 1, 
    professional: 2, 
    enterprise: 3 
  };
  
  const centerTierRank = tierRank[centerData.licenseLevel];
  const requiredTierRank = tierRank[feature.tier];
  
  if (centerTierRank === undefined) {
    console.warn(`canAccess: Unknown license level: ${centerData.licenseLevel}`);
    return false;
  }
  
  if (requiredTierRank === undefined) {
    console.warn(`canAccess: Unknown feature tier: ${feature.tier}`);
    return false;
  }
  
  return centerTierRank >= requiredTierRank;
};

/**
 * Get feature information
 * @param {string} featureName - Name of the feature
 * @returns {Object|null} - Feature configuration or null if not found
 */
export const getFeature = (featureName) => {
  return FEATURES[featureName] || null;
};

/**
 * Get all features for a specific tier
 * @param {string} tier - Tier name (essential, professional, enterprise)
 * @returns {Array} - Array of feature names for the tier
 */
export const getFeaturesByTier = (tier) => {
  return Object.keys(FEATURES).filter(
    featureName => FEATURES[featureName].tier === tier
  );
};

export default FEATURES;